import React, { useRef, useState } from 'react';
import { PaperClipIcon, PaperAirplaneIcon, MicrophoneIcon } from '@heroicons/react/24/outline';
import PdfGenerator from './PdfGenerator';

const ChatInput = ({
    inputValue,
    setInputValue,
    handleSend,
    handleImageChange,
    pdfStats,
    messages, 
    sentImageUrls, 
    setShowForm, 
    setShowModal, 
    setIsOver, 
    disabled,
    conversationId,
    refreshHistory,
    isConversationCompleted
}) => {
    const fileInputRef = useRef(null);
    const [isListening, setIsListening] = useState(false);

    // --- FONCTION DE DICTÉE VOCALE ---
    const startListening = () => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

        if (!SpeechRecognition) {
            alert("Votre navigateur ne supporte pas la reconnaissance vocale. Veuillez utiliser Chrome ou Edge.");
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.lang = 'fr-FR';
        recognition.continuous = false;
        recognition.interimResults = false;

        recognition.onstart = () => setIsListening(true);
        recognition.onend = () => setIsListening(false);

        recognition.onresult = (event) => {
            const transcript = event.results[0][0].transcript;
            setInputValue((prev) => (prev ? prev + " " + transcript : transcript));
        };

        recognition.onerror = (event) => {
            console.error("Erreur dictée:", event.error);
            setIsListening(false);
        };

        recognition.start();
    };

    const onSubmit = (e) => {
        e.preventDefault();
        if (!disabled && (inputValue.trim() || fileInputRef.current?.files?.length > 0)) {
            handleSend(e, inputValue);
        }
    };

    const quotaExhausted = pdfStats && pdfStats.remaining !== undefined && pdfStats.remaining <= 0;

    return (
        <div className="w-full relative pb-4 px-4">

            {/* PDF Generator (invisible - déclenché automatiquement par useEffect) */}
            <PdfGenerator
                messages={messages}
                setShowForm={setShowForm}
                sentImageUrls={sentImageUrls}
                setShowModal={setShowModal}
                setIsOver={setIsOver}
                conversationId={conversationId}
                refreshHistory={refreshHistory}
                isCompleted={isConversationCompleted}
            />

            {/* ALERTE QUOTA ATTEINT */}
            {quotaExhausted && (
                <div className="mb-3 mx-1 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl shadow-sm">
                    <svg className="w-5 h-5 flex-shrink-0 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <div className="text-sm">
                        <span className="font-semibold">Quota mensuel atteint.</span> Vous avez utilisé vos {pdfStats.total} crédits pour ce mois. Contactez votre administrateur pour augmenter votre quota.
                    </div>
                </div>
            )}

            {/* BARRE DE SAISIE */}
            <form
                onSubmit={onSubmit}
                className={`flex items-center gap-2 bg-white border rounded-full px-3 py-2 shadow-lg transition-all focus-within:ring-2 focus-within:ring-[#Ceaa5b]/50
                ${quotaExhausted ? 'opacity-50 cursor-not-allowed border-red-300' : (isListening ? 'border-red-500 ring-2 ring-red-200' : 'border-[#Ceaa5b] focus-within:border-[#Ceaa5b]')}`}
            >
                {/* 1. BOUTON FICHIER */}
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 text-gray-400 hover:text-[#1c2143] transition-colors rounded-full hover:bg-gray-100 flex-shrink-0"
                    title="Joindre un fichier"
                >
                    <PaperClipIcon className="w-6 h-6" />
                </button>

                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    className="hidden"
                    accept="image/*,application/pdf"
                    multiple
                />

                {/* 2. BOUTON MICROPHONE */}
                <button
                    type="button"
                    onClick={startListening}
                    className={`p-2 transition-colors rounded-full flex-shrink-0 
                        ${isListening 
                            ? 'text-red-500 bg-red-50 animate-pulse' 
                            : 'text-gray-400 hover:text-[#Ceaa5b] hover:bg-gray-100'
                        }`}
                    title="Dicter vocalement"
                >
                    <MicrophoneIcon className="w-6 h-6" />
                </button>

                {/* 3. CHAMP TEXTE */}
                <input
                    className={`
                        flex-1 bg-transparent border-none outline-none ring-0 focus:ring-0
                        text-[#1c2143] placeholder-gray-400 h-10 px-2
                        ${quotaExhausted ? 'cursor-not-allowed' : ''}
                    `}
                    readOnly={quotaExhausted}
                    disabled={quotaExhausted}
                    name="question"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={
                        quotaExhausted
                        ? "Quota mensuel atteint - Contactez votre administrateur"
                        : (isListening
                            ? "Je vous écoute..."
                            : "Posez vos questions ici...")
                    }
                    autoComplete="off"
                />

                {/* 4. BOUTON ENVOYER */}
                <button
                    type="submit"
                    disabled={disabled || quotaExhausted || (!inputValue.trim())}
                    className={`
                        p-2.5 rounded-full transition-all flex-shrink-0 flex items-center justify-center
                        ${inputValue.trim() && !disabled && !quotaExhausted
                            ? 'bg-[#1c2143] text-white hover:bg-[#2a3055] shadow-md transform hover:scale-105'
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }
                    `}
                >
                    <PaperAirplaneIcon className="w-5 h-5 -rotate-45 translate-x-0.5" />
                </button>
            </form>
        </div>
    );
};

export default ChatInput;